<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
  campaigns: {
    type: Array,
    default: () => [],
  },
});

const activeCampaigns = computed(() => {
  return props.campaigns.filter(c => c.status === 'active');
});

const calendarDays = computed(() => {
  const days = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let i = 0; i < 14; i++) {
    const date = new Date(today);
    date.setDate(date.getDate() + i);
    const wday = date.getDay();
    const dateStr = date.toISOString().split('T')[0];

    const scheduled = activeCampaigns.value.filter(c =>
      (c.schedule_days || []).includes(wday)
    );

    days.push({
      date: dateStr,
      dayOfMonth: date.getDate(),
      dayName: t(`OMNI.WA_CAMPAIGNS.DAYS.${wday}`),
      isToday: i === 0,
      campaigns: scheduled,
      count: scheduled.length,
    });
  }

  return days;
});
</script>

<template>
  <div class="bg-n-solid-2 border border-n-weak rounded-xl p-4">
    <h3 class="text-sm font-medium text-n-slate-11 mb-3 flex items-center gap-2">
      <span class="i-lucide-calendar size-4" />
      {{ t('OMNI.WA_CAMPAIGNS.CALENDAR.TITLE') }}
    </h3>

    <div class="flex gap-1.5 overflow-x-auto pb-1">
      <div
        v-for="day in calendarDays"
        :key="day.date"
        class="flex flex-col items-center min-w-[3.5rem] py-2 px-1.5 rounded-lg border transition-colors"
        :class="[
          day.isToday
            ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/20'
            : 'border-transparent',
          day.count > 0
            ? 'bg-green-50 dark:bg-green-900/10'
            : 'bg-n-alpha-2'
        ]"
        :title="day.campaigns.map(c => c.title).join(', ')"
      >
        <span class="text-[10px] font-medium text-n-slate-10 uppercase">
          {{ day.dayName }}
        </span>
        <span
          class="text-lg font-bold mt-0.5"
          :class="day.isToday ? 'text-woot-600 dark:text-woot-400' : 'text-n-slate-12'"
        >
          {{ day.dayOfMonth }}
        </span>
        <div v-if="day.count > 0" class="flex gap-0.5 mt-1">
          <span
            v-for="i in Math.min(day.count, 3)"
            :key="i"
            class="w-1.5 h-1.5 rounded-full bg-green-500"
          />
          <span v-if="day.count > 3" class="text-[8px] text-green-600 dark:text-green-400 font-bold">
            +{{ day.count - 3 }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
